/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global global, Office, self, window */

import { fixIdForOWA, setUIMessage } from "../helpers/utility";
import { getLocalizationStrings } from "../loc/Localization";
import { IUIStrings } from "../loc/IUIStrings";

let strings: IUIStrings = undefined;

Office.onReady(() => {
  // If needed, Office.js is ready to be called
});

/**
 * Shows a notification when the add-in command is executed.
 * @param event
 */
export function action(event: Office.AddinCommands.Event) {
  // Load localization strings
  strings = getLocalizationStrings(window.navigator.language);
  
  setUIMessage(strings.PreparingMailCloning, Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator);
  
  OfficeRuntime.auth.getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true }).then((bootstrapToken: string) => {
    if (Office.context.mailbox.item.getSharedPropertiesAsync) {
      // Old function - not working anymore - always returns failed status
      // Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, function(result) {
      //   if (result.status === Office.AsyncResultStatus.Succeeded && result.value !== "") {
      //     Office.context.mailbox.item.getSharedPropertiesAsync(
      //       {
      //         // Pass auth token along.
      //         asyncContext: result.value
      //       },
      //       function(result2) {
      //         let sharedProperties = result2.value;
      //         createNewMailViaService(bootstrapToken, event, sharedProperties.targetMailbox);
      //       }
      //     );
      //   }
      // });

      Office.context.mailbox.item.getSharedPropertiesAsync(result => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error("[clonemail.sicim.eu] Unable to get shared properties. Maybe the current folder or mailbox isn't shared.");
          console.error(result.error);
          
          setUIMessage("Unable to get shared mailbox address. Contact IT Department.", Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage);
          return;
        }

        const sharedProps = result.value;
        const targetMailbox = sharedProps.targetMailbox;

        createNewMailViaService(bootstrapToken, event, targetMailbox);
      });
    } else {
      createNewMailViaService(bootstrapToken, event, Office.context.mailbox.userProfile.emailAddress);
    }
  });
}

export async function createNewMailViaService(bootstrapToken: string, event?: Office.AddinCommands.Event, mailboxUpn?: string): Promise<void> {

  // Get the current selected mail message
  const item: Office.Item &
    Office.ItemCompose &
    Office.ItemRead &
    Office.Message &
    Office.MessageCompose &
    Office.MessageRead &
    Office.Appointment &
    Office.AppointmentCompose &
    Office.AppointmentRead = Office.context.mailbox.item;
  const messageId = item.itemId;

  try {
    setUIMessage(
      strings.CloningMail,
      Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator
    );

    let bootstrapToken: string = await OfficeRuntime.auth.getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true, forMSGraphAccess: true });
      // PROD: https://sicim-addins-functions.azurewebsites.net/api/CloneMessage
      // DEV: https://sicim-outlook-addin-svc.azurewebsites.net/api/CloneMessage
    const functionResult = await fetch('https://sicim-addins-functions.azurewebsites.net/api/CloneMessage',
    {
      method: 'POST',
      body: JSON.stringify({ itemId: messageId, upn: mailboxUpn}),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bootstrapToken}`
      }
    });

    if (functionResult && functionResult.ok) {
      const functionResultMessage = await functionResult.json();

      var fixedId = fixIdForOWA(functionResultMessage.id);
      Office.context.mailbox.displayMessageForm(fixedId);
  
      setUIMessage(strings.MailCloned, Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage); 
    } else {
      setUIMessage(strings.ErrorWhileCloning, Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage);
      console.log(await functionResult.text());
    }
    
    return;
  } catch (err) {
    setUIMessage(
      strings.AnErrorOccurred,
      Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage
    );
    console.error(err);
    return;
  }
}

function getGlobal() {
  return typeof self !== "undefined"
    ? self
    : typeof window !== "undefined"
    ? window
    : typeof global !== "undefined"
    ? global
    : undefined;
}

const g = getGlobal() as any;

// The add-in command functions need to be available in global scope
g.action = action;
